import { createContext, useContext, useCallback, useState } from "react";

const ThemeContext = createContext({ theme: "dark", setTheme: () => {}, toggle: () => {} });

const STORAGE_KEY = "clientflow.theme";

function readInitial() {
  // The inline bootstrap in /public/index.html has already applied the class to <html>
  // before React mounts, so just mirror what's on the element.
  if (typeof document === "undefined") return "dark";
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readInitial);

  const applyTheme = useCallback((next) => {
    const root = document.documentElement;
    if (next === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    root.style.colorScheme = next;
  }, []);

  const setTheme = useCallback((next) => {
    setThemeState(next);
    applyTheme(next);
    // Persist EVERY explicit choice — even "dark" — so the toggle is a
    // sticky preference. (We default to dark for first-time visitors via
    // the bootstrap in index.html, but we still want a future change of
    // the default to NOT silently flip users who have already toggled.)
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
  }, [applyTheme]);

  const toggle = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [theme, setTheme]);

  // NOTE: we deliberately do NOT subscribe to (prefers-color-scheme)
  // changes. With dark-as-default, following the OS would flip a
  // first-time visitor on a light-mode laptop straight to light right
  // after the page paints, defeating the new default. Users who want
  // light mode can use the toggle — and that choice is then remembered.

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
